import Swiper from 'swiper';
import { Autoplay, Keyboard } from 'swiper/modules';
// import 'swiper/css';

/**
 * Curved "fan" hero carousel.
 * Visual shape is driven entirely by slide.progress, see applyFanTransform.
 * Tune the look by editing FAN_CONFIG only.
 */
const FAN_CONFIG = {
  offsetX: 52,          // horizontal step per slide away from centre (px, at 250px ref width)
  arcHeight: 38,        // vertical drop per step this is what creates the upward arc
  rotateDeg: 10,         // rotation per step, toward centre
  scaleStep: 0.14,
  opacityStep: 0.28,
  blurStep: 1.6,         // px of blur added per step away from centre
  maxVisibleSteps: 2.4,    // Set 2 to view 3 slides. slides further than this are fully hidden
  refWidth: 90,         // matches the largest .lv-fan-slide width in CSS
};

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

function applyFanTransform(swiper) {
  const liveWidth = swiper.slides[0]?.offsetWidth || FAN_CONFIG.refWidth;
  const scaleFactor = liveWidth / FAN_CONFIG.refWidth;
  const offsetX = FAN_CONFIG.offsetX * scaleFactor;
  const arcHeight = FAN_CONFIG.arcHeight * scaleFactor;

  swiper.slides.forEach((slideEl) => {
    const progress = clamp(slideEl.progress, -FAN_CONFIG.maxVisibleSteps, FAN_CONFIG.maxVisibleSteps);
    const absProgress = Math.abs(progress);
    const hidden = absProgress >= FAN_CONFIG.maxVisibleSteps;

    const translateX = progress * offsetX;
    const translateY = absProgress * arcHeight;
    const rotate = progress * -FAN_CONFIG.rotateDeg;
    const scale = 1 - absProgress * FAN_CONFIG.scaleStep;
    const opacity = hidden ? 0 : clamp(1 - absProgress * FAN_CONFIG.opacityStep, 0, 1);
    const blur = absProgress * FAN_CONFIG.blurStep;

    slideEl.style.transform = `translate3d(${translateX}px, ${translateY}px, 0) rotate(${rotate}deg) scale(${scale})`;
    slideEl.style.filter = blur > 0.05 ? `blur(${blur}px)` : 'none';
    slideEl.style.opacity = opacity;
    slideEl.style.zIndex = 100 - Math.round(absProgress * 10);
    slideEl.style.pointerEvents = hidden ? 'none' : 'auto';
  });
}

function syncTransitionDuration(swiper, duration) {
  swiper.slides.forEach((slideEl) => {
    slideEl.style.transitionDuration = `${duration}ms`;
  });
}

// Only the centred slide's video plays; everything else is paused & reset.
function manageActiveVideo(swiper) {
  swiper.slides.forEach((slideEl, index) => {
    const video = slideEl.querySelector('video');
    if (!video) return;

    if (index === swiper.activeIndex) {
      video.currentTime = 0;
      const playPromise = video.play();
      if (playPromise?.catch) playPromise.catch(() => {});
    } else if (!video.paused) {
      video.pause();
    }
  });
}

export function initHeroFanCarousel(selector = '#lvFan') {
  const el = document.querySelector(selector);
  if (!el) return null;

  return new Swiper(el, {
    modules: [Autoplay, Keyboard],
    loop: true,
    loopAdditionalSlides: 3,
    centeredSlides: true,
    slidesPerView: 'auto',
    grabCursor: true,
    watchSlidesProgress: true,
    speed: 700,
    keyboard: { enabled: true, onlyInViewport: true },
    autoplay: {
      delay: 3200,
      disableOnInteraction: false,
      pauseOnMouseEnter: true, // handles "pause on hover, resume after" for free
    },
    on: {
      init(sw) {
        applyFanTransform(sw);
        manageActiveVideo(sw);
      },
      progress: applyFanTransform,
      setTransition: syncTransitionDuration,
      slideChangeTransitionEnd: manageActiveVideo,
    },
  });
}